/**
 * Filters used on any basic report, containing only the date range
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';

import session from 'containers/App/session';
import fiscalPeriodsModule from 'containers/App/data/endpoints/fiscalPeriods';
import messages from './messages';

import { FormattedMessage } from 'react-intl';
import { SearchOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import FiscalPeriodLabel from 'components/FiscalPeriodLabel';
import Button from 'components/forms/Button';
import PeriodSelectionModal from 'containers/shared/PeriodSelectionModal';
import { Space } from 'antd';

const Title = styled.h2`
    font-family: ${({ theme }) => theme.font.title};
    color: ${({ theme }) => theme.text};
`;

const PeriodContainer = styled.div`
    display: flex;
    align-items: center;
    height: 40px;

    > div {
        &:first-child {
            width: 40%;
            max-width: 400px;
            & > *:last-child {
                vertical-align: baseline;
                margin-left: 5px;
            }
        }

        &:not(:first-child) {
            margin-left: 15px;
        }
    }
`;

const SimpleReportFilters = ({
    period = null,
    onChangePeriod,
    onSubmit,
    showGetMyDataButton = true,
    title = null,
    showTitle = true,
    showLabel = true,
    maxDateRange = undefined,
    hidePointInTime = true,
}) => {
    const [isModalOpen, toggleModalOpened] = useState(false);

    const dispatch = useDispatch();
    const propertyId = useSelector(session.selectors.selectSelectedPropertyId);
    const fiscalPeriodsState = useSelector(fiscalPeriodsModule.selectors.selectState);

    const loadFiscalPeriods = () => {
        dispatch(fiscalPeriodsModule.actions.fetch(propertyId));
    };

    useEffect(() => {
        // First render
        loadFiscalPeriods();
        return () => {
            loadFiscalPeriods();
            toggleModalOpened(false);
            onChangePeriod(null);
        };
    }, [propertyId]);

    const onOpenPeriodSelectionModal = () => toggleModalOpened(true);
    const onClosePeriodSelectionModal = () => toggleModalOpened(false);
    const onSelectPeriod = (startDate, endDate, details, pointInTime) => {
        onChangePeriod({
            startDate,
            endDate,
            details,
            pointInTime: hidePointInTime ? null : pointInTime,
        });
        toggleModalOpened(false);
    };

    let periodDisplay = null;
    if (period == null) {
        periodDisplay = (
            <Button
                name="periodSelectionBtn"
                type="primary"
                icon={<PlusOutlined />}
                onClick={onOpenPeriodSelectionModal}
            >
                <FormattedMessage {...messages.choosePeriodButton} />
            </Button>
        );
    } else {
        periodDisplay = (
            <>
                <b>
                    <FiscalPeriodLabel {...period} />
                </b>
                <Button
                    name="periodSelectionBtn"
                    icon={<EditOutlined />}
                    style={{ marginLeft: '10px' }}
                    onClick={onOpenPeriodSelectionModal}
                >
                    <FormattedMessage {...messages.changePeriodButton} />
                </Button>
            </>
        );
    }

    return (
        <Space>
            <PeriodSelectionModal
                visible={isModalOpen}
                onConfirm={onSelectPeriod}
                onCancel={onClosePeriodSelectionModal}
                periods={{
                    loading: fiscalPeriodsState.fetching,
                    error: fiscalPeriodsState.error,
                    data: fiscalPeriodsState.data,
                    retry: loadFiscalPeriods,
                }}
                hidePointInTime={hidePointInTime}
                maxDateRange={maxDateRange}
            />
            {showTitle && (
                <Title>
                    {!title && <FormattedMessage {...messages.title} />}
                    {title && title}
                </Title>
            )}
            <PeriodContainer>
                {showLabel && <FormattedMessage tagName="div" {...messages.periodSelectionLabel} />}
                <span>{periodDisplay}</span>
            </PeriodContainer>
            <br />
            {showGetMyDataButton && (
                <Button
                    name="submitBtn"
                    type="primary"
                    size="large"
                    icon={<SearchOutlined />}
                    onClick={onSubmit}
                    disabled={period === null}
                >
                    <FormattedMessage {...messages.getDataButton} />
                </Button>
            )}
        </Space>
    );
};

export default SimpleReportFilters;
